import PropTypes from 'prop-types';

import { Link } from 'react-router';

import { useAppDispatch, useAppSelector } from 'dux/app/hooks';

import { Box } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';
import { Formik } from 'formik';

import { ProgressTracker } from 'Components/ProgressTracker';
import Spacer from 'Components/Spacer';
import { TextField } from 'Components/TextField';
import Title from 'Components/Title';
import { Typography } from 'Components/Typography';
import { ConsultationFooter } from '../Components/ConsultationFooter';

import { useHomepageEmailCapturePopUpAbTest } from 'abTesting/homepageEmailCapturePopUp/hook';

import { srcOrNextImageProp } from 'PropTypes/imageProps';

import { signupAcceptTermsTitle as signupAcceptTermsTitleDI } from 'assets/content/consultation/consultation';

import useResponsiveVariant from 'utils/useResponsiveVariant';
import { makeValidations } from 'utils/validators';

import { getConsultationSignUpPromotionsOverride } from 'dux/questionSets/selectors';
import { getHasSignedUpWithoutNames } from 'dux/user/selectors';
import { signoutUser } from 'dux/auth/thunks';
import { VARIANT1 } from 'dux/featureFlags/constants/flagVariants';

import { useConsultationCategory } from '../hooks/useConsultationCategory';
import { useConsultationSubsetCategory } from '../hooks/useConsultationSubsetCategory';
import BaseScene from './BaseScene';

const Paragraph = styled(Typography)`
  max-width: 449px;
  ${legacyTheme.breakpoints.up('md')} {
    max-width: unset;
  }
`;

const Container = styled.div`
  max-width: 367px;

  display: flex;
  flex-direction: column;
  align-items: center;
  flex: ${({ shouldStretch }) => (shouldStretch ? 1 : 'unset')};

  ${legacyTheme.breakpoints.up('md')} {
    max-width: unset;
  }
`;

const QuestionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1 0 auto;
  max-width: 367px;
`;

const StyledTextField = styled(TextField)`
  width: 90vw;
  max-width: ${({ maxWidth }) => maxWidth};
`;

export const Signup = ({
  acceptTermsMode,
  content,
  handleSubmit,
  initialValues,
  questionIndex,
  routeParams,
  signupAcceptTermsTitle = signupAcceptTermsTitleDI,
}) => {
  const dispatch = useAppDispatch();
  const consultationCategory = useConsultationCategory();
  const consultationSubsetCategory = useConsultationSubsetCategory();
  const { isDesktop } = useResponsiveVariant();

  const showTermsInFooter = isDesktop;
  const consultationSignUpPromotionsOverride = useAppSelector(state =>
    getConsultationSignUpPromotionsOverride(state, { consultationCategory })
  );
  const hasSignedUpWithoutNames = useAppSelector(getHasSignedUpWithoutNames);
  const { variantName: homepageEmailCapturePopUpAbTestVariant } =
    useHomepageEmailCapturePopUpAbTest();
  const isInHomepageEmailCaptureExperiment =
    hasSignedUpWithoutNames && homepageEmailCapturePopUpAbTestVariant === VARIANT1;

  const displayedContent = {
    ...content,
    ...consultationSignUpPromotionsOverride,
    subtitle: isInHomepageEmailCaptureExperiment
      ? 'Complete your profile and finish the consultation to unlock your exclusive offer.'
      : consultationSignUpPromotionsOverride.subtitle || content.subtitle,
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={values =>
        makeValidations(values, [
          ['firstName', 'required'],
          ['firstName', 'firstName'],
          ['lastName', 'required'],
          ['lastName', 'lastName'],
          ['email', 'required'],
          ['email', 'email'],
        ])
      }
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit: handleSubmitFormik,
        isSubmitting,
        touched,
        values,
      }) => (
        <form onSubmit={handleSubmitFormik}>
          <BaseScene>
            <ProgressTracker
              consultationCategory={consultationCategory}
              consultationSubsetCategory={consultationSubsetCategory}
              questionIndex={questionIndex}
              routeParams={routeParams}
            />

            <Container shouldStretch={false}>
              <Title dataTestid="signup-title" forceLarge>
                {acceptTermsMode ? signupAcceptTermsTitle : displayedContent.title}
              </Title>

              {displayedContent.subtitle && (
                <Paragraph align="center" color="grey" variant="p2">
                  {displayedContent.subtitle}
                </Paragraph>
              )}

              <Spacer axis="vertical" size={24} />

              <QuestionWrapper>
                {/* Firstname */}

                <StyledTextField
                  aria-invalid={errors.firstName ? 'true' : 'false'}
                  aria-required="true"
                  autoCorrect={false}
                  disabled={isSubmitting}
                  error={
                    touched.firstName &&
                    errors.firstName &&
                    `First name ${errors.firstName.toLowerCase()}`
                  }
                  id="firstName"
                  label="First name"
                  margin={false}
                  name="firstName"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  value={values.firstName}
                />

                {/* Lastname */}
                <StyledTextField
                  aria-invalid={errors.lastName ? 'true' : 'false'}
                  aria-required="true"
                  autoCorrect={false}
                  disabled={isSubmitting}
                  error={
                    touched.lastName &&
                    errors.lastName &&
                    `Last name ${errors.lastName.toLowerCase()}`
                  }
                  id="lastName"
                  label="Last name"
                  margin={false}
                  name="lastName"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  value={values.lastName}
                />

                {/* Email */}
                <StyledTextField
                  aria-invalid={errors.email ? 'true' : 'false'}
                  aria-required="true"
                  autoCorrect={false}
                  disabled={isSubmitting || isInHomepageEmailCaptureExperiment}
                  error={
                    touched.email && errors.email && `Email address ${errors.email.toLowerCase()}`
                  }
                  id="email"
                  label="Email address"
                  margin={false}
                  name="email"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="email"
                  value={values.email}
                />
              </QuestionWrapper>

              {!isInHomepageEmailCaptureExperiment &&
                !showTermsInFooter &&
                displayedContent.terms &&
                values.email.length > 0 && (
                  <Typography align="center" color="grey" variant="p2">
                    {displayedContent.terms}
                  </Typography>
                )}

              {!isInHomepageEmailCaptureExperiment &&
                (displayedContent.signinQuestion || displayedContent.signinCta) && (
                  <>
                    <Spacer size={56} />

                    <Box display="flex" flexGrow="1" justifyContent="center">
                      {displayedContent.signinQuestion && (
                        <Typography align="center" color="grey" variant="p2">
                          {displayedContent.signinQuestion}{' '}
                          {displayedContent.signinCta && (
                            <Link to="/signin">{displayedContent.signinCta}</Link>
                          )}
                        </Typography>
                      )}
                    </Box>
                  </>
                )}
              {isInHomepageEmailCaptureExperiment && (
                <>
                  <Spacer size={56} />

                  <Box display="flex" flexGrow="1" justifyContent="center">
                    <Typography align="center" color="grey" variant="p2">
                      Not your email address? <br />
                      {displayedContent.signinCta && (
                        <a
                          data-click="log-out"
                          data-from="consultation-name"
                          href="/signin"
                          onClick={() => dispatch(signoutUser())}
                        >
                          Log out
                        </a>
                      )}
                    </Typography>
                  </Box>
                </>
              )}
            </Container>

            <ConsultationFooter
              buttonLabel="Next"
              buttonType="submit"
              disabled={
                isSubmitting ||
                (isInHomepageEmailCaptureExperiment &&
                  (!values.firstName || !values.lastName || errors.firstName || errors.lastName))
              }
              isSaving={isSubmitting}
              terms={showTermsInFooter && content.terms}
            />
          </BaseScene>
        </form>
      )}
    </Formik>
  );
};

Signup.propTypes = {
  acceptTermsMode: PropTypes.bool,
  content: PropTypes.shape({
    name: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    terms: PropTypes.node,
    signinQuestion: PropTypes.string,
    signinCta: PropTypes.string,
    descriptionImage: PropTypes.shape({
      alt: PropTypes.string,
      height: PropTypes.number,
      source: srcOrNextImageProp,
      width: PropTypes.number,
    }),
  }).isRequired,
  initialValues: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
  }).isRequired,
  handleSubmit: PropTypes.func.isRequired,
  questionIndex: PropTypes.number.isRequired,
  routeParams: PropTypes.shape({}),
  signupAcceptTermsTitle: PropTypes.string,
};

Signup.defaultProps = {
  acceptTermsMode: false,
  routeParams: null,
  signupAcceptTermsTitle: signupAcceptTermsTitleDI,
};

export default Signup;
